import { Box } from "@mui/material";
import { ForwardedRef, forwardRef, useEffect, useRef } from "react";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialogContext.tsx";
import i18n from "i18next";
import { CenteredTypography } from "../CenteredTypography";
import { Coordinates } from "src/api/generated/common/address/coordinates.ts";
import {
	defaultProximity,
	toGoogleLatLng,
} from "src/components/common/googleMaps/googleMapsUtils.ts";
import { GOOGLE_MAPS_MAP_ID, loadGoogleMapsLibrary } from "src/components/common/googleMaps/googleMapsApi.ts";
import { getGoogleMapsBrowserApiKey } from "src/config/appConfig.ts";

export interface GoogleMapViewProps {
	options?: Omit<google.maps.MapOptions, "center">;
	center?: Coordinates;
	onLoaded?: (map: google.maps.Map) => Promise<unknown> | void;
}

export const GoogleMapView = forwardRef(
	(
		{ options, center = defaultProximity, onLoaded }: GoogleMapViewProps,
		externalRef: ForwardedRef<google.maps.Map>,
	) => {
		const mapContainerRef = useRef<HTMLDivElement | null>(null);
		const mapRef = useRef<google.maps.Map | null>(null);

		const { logErrorAndShowOnDialog } = useErrorDialog();

		useEffect(() => {
			if (mapRef.current) return;
			const mapContainer = mapContainerRef.current;
			if (mapContainer === null) {
				console.log("Cannot initialize map because container ref is null");
				return;
			}
			const mapElement = mapContainer;

			let disposed = false;

			async function initializeMap() {
				try {
					const { Map } = await loadGoogleMapsLibrary("maps");
					if (disposed) return;

					const map = new Map(mapElement, {
						center: toGoogleLatLng(center),
						zoom: 8,
						mapId: GOOGLE_MAPS_MAP_ID,
						mapTypeControl: false,
						streetViewControl: false,
						fullscreenControl: false,
						...options,
					});

					mapRef.current = map;
					if (typeof externalRef === "function") {
						externalRef(map);
					} else if (externalRef != null) {
						externalRef.current = map;
					}

					await onLoaded?.(map);
				} catch (e) {
					console.error(e);
					logErrorAndShowOnDialog(e);
				}
			}

			void initializeMap();

			return () => {
				disposed = true;
				mapRef.current = null;
				if (typeof externalRef === "function") {
					externalRef(null);
				} else if (externalRef != null) {
					externalRef.current = null;
				}
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		if (getGoogleMapsBrowserApiKey() === "")
			return <CenteredTypography>{i18n.t("maps_disabled_message")}</CenteredTypography>;

		return (
			<Box
				ref={mapContainerRef}
				sx={{
					flex: 1,
					display: "flex",
				}}
			/>
		);
	},
);
